import React, { useState, useEffect } from "react";
import { Bar, Pie, Line, Doughnut, Radar } from "react-chartjs-2";
import { Row, Col, Card, Form, Modal, Button } from "react-bootstrap";
import { useSpring, animated, config } from "@react-spring/web";
import axios from "axios";
import styles from "./styles.module.css";

const VFCDash = () => {
  const [showModal, setShowModal] = useState(false);
  const [circles, setCircles] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [ranges, setRanges] = useState([]);
  const [vfcs, setVfcs] = useState([]);
  const [animate, setAnimate] = useState(false);
  const [viewCharts, setViewCharts] = useState(false);
  const [notificationContent, setNotificationContent] = useState([]);
  const [selectedCircle, setSelectedCircle] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedRange, setSelectedRange] = useState("");
  const [selectedVfc, setSelectedVfc] = useState("");
  const [state, toggle] = useState(true);
  const Api = process.env.REACT_APP_API;

  useEffect(() => {
    // Fetch initial circle data
    axios.get(`${Api}/api/circles`).then((response) => {
      setCircles(response.data);
    });
  }, []);

  useEffect(() => {
    if (selectedCircle) {
      axios
        .post(`${Api}/api/divisions/divisions-by-circle`, {
          circleId: selectedCircle,
        })
        .then((response) => {
          setDivisions(response.data.divisions);
        });
    }
  }, [selectedCircle]);

  useEffect(() => {
    if (selectedDivision) {
      axios
        .post(`${Api}/api/ranges/ranges-by-division`, {
          divisionId: selectedDivision,
        })
        .then((response) => {
          setRanges(response.data.ranges);
        });
    }
  }, [selectedDivision]);

  useEffect(() => {
    if (selectedRange) {
      axios
        .post(`${Api}/api/vfcs/vfcs-by-range`, {
          rangeId: selectedRange,
        })
        .then((response) => {
          setVfcs(response.data.vfcs);
        });
    }
  }, [selectedRange]);

  const handleModalShow = () => setShowModal(true);
  const handleModalClose = () => setShowModal(false);

  const barData = {
    labels: ["VFC 1", "VFC 2", "VFC 3", "VFC 4"],
    datasets: [
      {
        label: "Performance",
        data: [65, 59, 80, 81],
        backgroundColor: "rgba(75,192,192,0.4)",
        borderColor: "rgba(75,192,192,1)",
        borderWidth: 1,
      },
    ],
  };

  const pieData = {
    labels: ["VFC 1", "VFC 2", "VFC 3"],
    datasets: [
      {
        data: [300, 50, 100],
        backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56"],
      },
    ],
  };

  const lineData = {
    labels: ["January", "February", "March", "April"],
    datasets: [
      {
        label: "Revenue",
        data: [65, 59, 80, 81],
        fill: false,
        borderColor: "rgba(75,192,192,1)",
        tension: 0.1,
      },
    ],
  };

  const doughnutData = {
    labels: ["VFC A", "VFC B", "VFC C"],
    datasets: [
      {
        data: [200, 150, 100],
        backgroundColor: ["#FF9F40", "#4BC0C0", "#9966FF"],
      },
    ],
  };

  const radarData = {
    labels: ["Strength", "Speed", "Skill", "Endurance", "Agility"],
    datasets: [
      {
        label: "VFC Attributes",
        data: [65, 59, 90, 81, 56],
        backgroundColor: "rgba(255,99,132,0.2)",
        borderColor: "rgba(255,99,132,1)",
        borderWidth: 1,
      },
    ],
  };

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28"];
  const notificationAnimation = useSpring({
    transform: animate ? "translateX(0%)" : "translateX(100%)",
    opacity: animate ? 1 : 0,
    backgroundColor: animate ? "#ffeb3b" : "#fff176",
    scale: animate ? 1.05 : 1,
    config: config.stiff,
  });

  const colorPulse = useSpring({
    loop: true,
    to: { backgroundColor: "#dc3545" },
    from: { backgroundColor: "#ffeb3b" },
    config: { duration: 1000 },
  });

  const { x } = useSpring({
    from: { x: 0 },
    x: state ? 1 : 0,
    config: { duration: 1000 },
  });

  const totalFamilies = vfcs.reduce((acc, vfc) => vfc.totalFamily, 0);
  const totalMembers = vfcs.reduce((acc, vfc) => acc + vfc.totalMembers, 0);
  const totalSHG = vfcs.reduce((acc, vfc) => acc + vfc.totalSHG, 0);
  const totalLoan = vfcs.reduce((acc, vfc) => acc + vfc.totalLoanAmount, 0);
  const handleViewClick = () => {
    if (selectedRange && selectedVfc) {
      setViewCharts(true);
      setAnimate(true);
    } else {
      setViewCharts(false);
    }
  };
  return (
    <div>
      <div className="initial-overlay" onClick={handleModalShow}>
        <div className={styles.container} onClick={() => toggle(!state)}>
          <animated.div
            className={styles.text}
            style={{
              opacity: x.to({ range: [0, 1], output: [0.3, 1] }),
              scale: x.to({
                range: [0, 0.25, 0.35, 0.45, 0.55, 0.65, 0.75, 1],
                output: [1, 0.97, 0.9, 1.1, 0.9, 1.1, 1.03, 1],
              }),
            }}
          >
            click
          </animated.div>
        </div>
      </div>

      <Modal show={showModal} onHide={handleModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Select VFC Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Circle</Form.Label>
              <Form.Control
                as="select"
                value={selectedCircle}
                onChange={(e) => setSelectedCircle(e.target.value)}
              >
                <option value="">Select Circle</option>
                {circles.map((circle) => (
                  <option key={circle._id} value={circle._id}>
                    {circle.circleName}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Division</Form.Label>
              <Form.Control
                as="select"
                value={selectedDivision}
                onChange={(e) => setSelectedDivision(e.target.value)}
                disabled={!selectedCircle}
              >
                <option value="">Select Division</option>
                {divisions.map((division) => (
                  <option key={division._id} value={division._id}>
                    {division.divisionName}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Range</Form.Label>
              <Form.Control
                as="select"
                value={selectedRange}
                onChange={(e) => setSelectedRange(e.target.value)}
                disabled={!selectedDivision}
              >
                <option value="">Select Range</option>
                {ranges.map((range) => (
                  <option key={range._id} value={range._id}>
                    {range.rangeName}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>VFC</Form.Label>
              <Form.Control
                as="select"
                value={selectedVfc}
                onChange={(e) => setSelectedVfc(e.target.value)}
                disabled={!selectedRange}
              >
                <option value="">Select VFC</option>
                {vfcs.map((vfc) => (
                  <option key={vfc._id} value={vfc._id}>
                    {vfc.vfcName}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              handleModalClose();
              handleViewClick();
            }}
            disabled={!selectedVfc}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
      {viewCharts && (
        <>
          <div className="page-header">
            <h3 className="page-title"> VFC Dashboard </h3>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="!#" onClick={(event) => event.preventDefault()}>
                    VFC
                  </a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  VFC Dashboard
                </li>
              </ol>
            </nav>
          </div>

          <Row>
            <Col xs={6}>
              {/* <Row>
                <Col xs={6}>
                  <Card
                    className="text-white"
                    style={{ backgroundColor: "#007bff" }}
                  >
                    <Card.Body>
                      <Card.Title>Total Family</Card.Title>
                      <Card.Text>{totalFamilies}</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6}>
                  <Card
                    className="text-white"
                    style={{ backgroundColor: "#28a745" }}
                  >
                    <Card.Body>
                      <Card.Title>Total Members</Card.Title>
                      <Card.Text>{totalMembers}</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              </Row> */}
              <div className="row">
                <div className="col-md-6 stretch-card grid-margin">
                  <div className="card bg-gradient-danger card-img-holder text-white">
                    <div className="card-body">
                      <img
                        src={require("../../assets/images/dashboard/circle.png")}
                        className="card-img-absolute"
                        alt="circle"
                      />
                      <h4 className="font-weight-normal mb-3">
                        Total Family
                        <i className="mdi mdi-chart-line mdi-24px float-right"></i>
                      </h4>
                      <h2 className="mb-5">{totalFamilies}</h2>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 stretch-card grid-margin">
                  <div className="card bg-gradient-info card-img-holder text-white">
                    <div className="card-body">
                      <img
                        src={require("../../assets/images/dashboard/circle.png")}
                        className="card-img-absolute"
                        alt="circle"
                      />
                      <h4 className="font-weight-normal mb-3">
                        Total Members
                        <i className="mdi mdi-bookmark-outline mdi-24px float-right"></i>
                      </h4>
                      <h2 className="mb-5">{totalMembers}</h2>
                    </div>
                  </div>
                </div>
              </div>
              <Row>
                {/* <Col xs={6}>
                  <Card
                    className="text-white"
                    style={{ backgroundColor: "#ffc107" }}
                  >
                    <Card.Body>
                      <Card.Title>Total SHG</Card.Title>
                      <Card.Text>{totalSHG}</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6}>
                  <Card
                    className="text-white"
                    style={{ backgroundColor: "#dc3545" }}
                  >
                    <Card.Body>
                      <Card.Title>Total Loan</Card.Title>
                      <Card.Text>${totalLoan.toFixed(2)}</Card.Text>
                    </Card.Body>
                  </Card>
                </Col> */}

                <div className="col-md-6 stretch-card grid-margin">
                  <div className="card bg-gradient-danger card-img-holder text-white">
                    <div className="card-body">
                      <img
                        src={require("../../assets/images/dashboard/circle.png")}
                        className="card-img-absolute"
                        alt="circle"
                      />
                      <h4 className="font-weight-normal mb-3">
                        Total SHG
                        <i className="mdi mdi-chart-line mdi-24px float-right"></i>
                      </h4>
                      <h2 className="mb-5">{totalSHG}</h2>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 stretch-card grid-margin">
                  <div className="card bg-gradient-success card-img-holder text-white">
                    <div className="card-body">
                      <img
                        src={require("../../assets/images/dashboard/circle.png")}
                        className="card-img-absolute"
                        alt="circle"
                      />
                      <h4 className="font-weight-normal mb-3">
                        Total Loan
                        <i className="mdi mdi-diamond mdi-24px float-right"></i>
                      </h4>
                      <h2 className="mb-5">
                        <i className="mdi mdi-currency-inr">
                          {totalLoan.toFixed(2)}
                        </i>
                      </h2>
                    </div>
                  </div>
                </div>
              </Row>
            </Col>
            <Col xs={6} className="notification-container">
              <animated.div
                style={{
                  ...notificationAnimation,
                  ...colorPulse,
                  padding: "10px",
                  borderRadius: "5px",
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                }}
                className="notification-div"
              >
                {notificationContent.length > 0 ? (
                  notificationContent.map((msg, index) => (
                    <div key={index}>{msg}</div>
                  ))
                ) : (
                  <p>No notifications at the moment</p>
                )}
              </animated.div>
            </Col>
          </Row>

          <Row>
            <Col xs={6}>
              <h4>Your VFC Ratting</h4>
              <Radar data={radarData} />
            </Col>
            <Col xs={6}>
              <h4>Loan Status</h4>
              <Doughnut data={doughnutData} />
            </Col>
          </Row>

          <Row>
            <Col xs={6}>
              <h4>Loan Issue & Loan Repayment</h4>
              <Bar data={barData} />
            </Col>
            <Col xs={6}>
              <h4>Performance Evaluation</h4>
              <Pie data={pieData} />
            </Col>
          </Row>
          <h4>Loan Issue Graph</h4>

          <Line data={lineData} />
        </>
      )}
    </div>
  );
};

export default VFCDash;
